import { Breadcrumbs as MuiBreadcrumbs, Link, Typography } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { NAV_ITEMS } from '../nav';
import { titleCase } from '../utils/format';
import { useBreadcrumbNames } from './breadcrumbNames';

const SEGMENT_LABEL: Record<string, string> = {
  incidents: 'Incidents',
  recommendations: 'Recommendations',
  outcomes: 'Outcomes',
  vehicles: 'Vehicles',
  'drivers-hours-rulesets': 'Drivers’ hours rulesets',
  policies: 'Policies',
  prompts: 'Prompt Registry',
  executions: 'LLM Executions',
  audit: 'Audit',
  admin: 'Administration',
  providers: 'Provider Connections',
  'inbound-events': 'Inbound events',
  settings: 'Tenant Settings',
  members: 'Members',
  // Personal scope, deliberately NOT under `settings` — that segment already
  // means "Tenant Settings", and "Tenant Settings / Notifications" would name
  // the wrong owner for a screen only its owner may change (D29).
  account: 'My Account',
  notifications: 'Notifications',
  branding: 'Branding',
  units: 'Regional defaults',
  platform: 'Platform',
  tenants: 'Tenants',
  'white-labels': 'Partners',
  // The partner console's own scope. Without this the segment falls through
  // to the id-shortening branch and renders as "Partner" by accident rather
  // than by decision — and `companies` would read as a tenant-scoped page.
  partner: 'Partner',
  resellers: 'Resellers',
  companies: 'Companies',
  people: 'People',
  users: 'Users', // platform scope only — tenant scope uses `members`
  // Without these the segments fall through to the id-shortening branch below
  // and render as "pipeline…" / "can-inte…", which read like truncated UUIDs.
  // `titleCase` would not save them either — the fallback measures length first.
  'pipeline-health': 'Pipeline Health',
  logs: 'System Logs',
  alerts: 'Alerts',
  'log-dashboard': 'Log Dashboard',
  'can-intelligence': 'CAN intelligence',
  'can-operations': 'CAN operations',
  tachograph: 'Drivers’ hours',
  anomalies: 'Anomaly detection',
  news: 'Fleet news',
  operations: 'Operations',
};

/**
 * Top-level routes that are NOT in `NAV_ITEMS`.
 *
 * ⚠️ **`settings` MEANS TWO DIFFERENT PAGES depending on where it sits**, and `SEGMENT_LABEL` is
 * keyed by the segment alone so it cannot tell them apart: `/admin/settings` is Tenant Settings,
 * and `/settings` is the Settings area the navigation redesign added. Declaring the second here —
 * consulted at index 0 only, beside the nav lookup that already runs there — keeps both correct.
 *
 * A duplicate key in `SEGMENT_LABEL` was the first attempt. It compiled to "last one wins" under
 * the bundler and the whole web SUITE passed; only `tsc` caught it (TS1117), which is the one
 * check that reads the object literal rather than its result.
 */
const FOOTER_LABEL: Record<string, string> = {
  settings: 'Settings',
};

function labelFor(segment: string, index: number, names: Readonly<Record<string, string>>): string {
  if (index === 0) {
    const nav = NAV_ITEMS.find((n) => n.path === `/${segment}`);
    if (nav) return nav.label;
    const footer = FOOTER_LABEL[segment];
    if (footer) return footer;
  }
  if (SEGMENT_LABEL[segment]) return SEGMENT_LABEL[segment];

  /*
   * A record the page has told us the name of. Consulted AFTER the static
   * vocabulary, never before: a published name must not be able to rename
   * `platform` or `vehicles`, and keying on the raw segment means a record
   * whose id happened to equal a route word would otherwise do exactly that.
   */
  const published = names[segment];
  /*
   * Truncated at 32, because a name is free text a customer chose and the trail
   * has to survive it. The id fallback below already shortens for the same
   * reason — a long final crumb wraps and pushes the rest off a narrow screen,
   * which is the layout fault this component exists to avoid rather than cause.
   */
  if (published !== undefined) {
    return published.length > 32 ? `${published.slice(0, 31)}…` : published;
  }

  // an id segment nobody has named — show a shortened form
  return segment.length > 12 ? `${segment.slice(0, 8)}…` : titleCase(segment);
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const names = useBreadcrumbNames();
  const segments = pathname.split('/').filter(Boolean);
  return (
    <MuiBreadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ fontSize: '0.8rem' }}>
      <Link component={RouterLink} to="/" underline="hover" color="inherit">
        Home
      </Link>
      {segments.map((seg, i) => {
        const to = `/${segments.slice(0, i + 1).join('/')}`;
        const last = i === segments.length - 1;
        const label = labelFor(seg, i, names);
        return last ? (
          <Typography key={to} color="text.primary" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        ) : (
          <Link key={to} component={RouterLink} to={to} underline="hover" color="inherit">
            {label}
          </Link>
        );
      })}
    </MuiBreadcrumbs>
  );
}
